/** Dispatched when the user toggles practice vs patient schedule targets (session preference). */
export const PATIENT_SCHEDULE_TARGET_PREF_EVENT = 'rpm:patient-schedule-target-pref';

const storageKey = (practiceId, patientId) =>
  `rpm_use_patient_schedule_targets:v1:${String(practiceId)}:${String(patientId)}`;

/** Millisecond timeline so historical readings categorize by mode active at reading time (this browser). */
const timelineKey = (practiceId, patientId) =>
  `rpm_schedule_target_mode_timeline:v1:${String(practiceId)}:${String(patientId)}`;

function readTimeline(practiceId, patientId) {
  if (practiceId == null || patientId == null) return [];
  try {
    const raw = localStorage.getItem(timelineKey(practiceId, patientId));
    const arr = raw ? JSON.parse(raw) : [];
    if (!Array.isArray(arr)) return [];
    return arr
      .filter((e) => e && typeof e.t === 'number' && typeof e.usePatient === 'boolean')
      .sort((a, b) => a.t - b.t);
  } catch {
    return [];
  }
}

function appendTimelineEntry(practiceId, patientId, usePatient) {
  if (practiceId == null || patientId == null) return;
  try {
    const entries = readTimeline(practiceId, patientId);
    const t = Date.now();
    const last = entries[entries.length - 1];
    if (last && last.usePatient === usePatient && t - last.t < 1500) return;
    entries.push({ t, usePatient });
    localStorage.setItem(timelineKey(practiceId, patientId), JSON.stringify(entries));
  } catch {
    /* ignore */
  }
}

/**
 * Whether patient-level schedule/target rows should apply for categorization at measurement time `ms`.
 * Uses a local timeline written on each preference change. If the timeline is empty, defaults to
 * practice mode for past readings (so switching to patient today does not re-label yesterday).
 */
export function getUsePatientScheduleTargetsAtMeasurementMs(practiceId, patientId, ms) {
  if (practiceId == null || patientId == null || ms == null || Number.isNaN(Number(ms))) return false;
  const entries = readTimeline(practiceId, patientId);
  let last = false;
  for (const e of entries) {
    if (e.t <= ms) last = e.usePatient;
    else break;
  }
  return last;
}

export function getUsePatientScheduleTargetsPreference(practiceId, patientId) {
  if (practiceId == null || patientId == null) return false;
  try {
    return sessionStorage.getItem(storageKey(practiceId, patientId)) === '1';
  } catch {
    return false;
  }
}

/** Last saved practice vs custom choice for this patient. Null when none has been saved in this browser. */
export function readPatientScheduleTargetMode(practiceId, patientId) {
  if (practiceId == null || patientId == null) return null;
  const entries = readTimeline(practiceId, patientId);
  if (entries.length) {
    return entries[entries.length - 1].usePatient ? 'custom' : 'practice';
  }
  return getUsePatientScheduleTargetsPreference(practiceId, patientId) ? 'custom' : null;
}

const vitalModeKey = (practiceId, patientId, vital) =>
  `rpm_vital_schedule_mode:v1:${String(practiceId)}:${String(patientId)}:${String(vital)}`;

/** Practice vs custom for one monitoring type. Falls back to the shared patient choice. */
export function readVitalScheduleTargetMode(practiceId, patientId, vital) {
  if (practiceId == null || patientId == null || !vital) return null;
  try {
    const specific = localStorage.getItem(vitalModeKey(practiceId, patientId, vital));
    if (specific === 'custom' || specific === 'practice') return specific;
  } catch {
    /* ignore */
  }
  return readPatientScheduleTargetMode(practiceId, patientId);
}

export function writeVitalScheduleTargetMode(practiceId, patientId, vital, mode) {
  if (practiceId == null || patientId == null || !vital) return;
  if (mode !== 'custom' && mode !== 'practice') return;
  try {
    localStorage.setItem(vitalModeKey(practiceId, patientId, vital), mode);
  } catch {
    /* ignore quota / private mode */
  }
}

export function setUsePatientScheduleTargetsPreference(practiceId, patientId, enabled) {
  if (practiceId == null || patientId == null) return;
  try {
    if (enabled) {
      sessionStorage.setItem(storageKey(practiceId, patientId), '1');
    } else {
      sessionStorage.removeItem(storageKey(practiceId, patientId));
    }
  } catch {
    /* ignore quota / private mode */
  }
  appendTimelineEntry(practiceId, patientId, !!enabled);
  try {
    window.dispatchEvent(
      new CustomEvent(PATIENT_SCHEDULE_TARGET_PREF_EVENT, {
        detail: { practiceId: String(practiceId), patientId: String(patientId) }
      })
    );
  } catch {
    /* noop */
  }
}
